/**
 * MyTimeOffPage — Vacaciones + permisos del colaborador.
 *
 * Usada en ambos contextos:
 *   - Caso A: /colaboradores/time-off (PortalLayout)
 *   - Caso B: /portal/time-off (AppLayout, "Mi Portal")
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, FileText, Plus, Loader2, Trash2 } from 'lucide-react'
import {
  usePortalVacationBalance, usePortalVacationRequests, usePortalBusinessDays,
  useCreateVacationRequest, useCancelVacationRequest, usePortalBase,
  usePortalLeaveRequests, useCreateLeaveRequest, useCancelLeaveRequest,
  type LeaveTypeOption, type VacationRequestItem, type LeaveRequestItem,
} from '../hooks/useEmployeePortal'
import { extractErrorMessage } from '@/lib/errorUtils'
import DatePicker from '@/components/DatePicker'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/ds/EmptyState'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { SegmentGroup } from '@/components/ds/SegmentGroup'
import { PaginaPortal } from '../components/PaginaPortal'
import { dias, fechaCorta } from '../components/formatos'
import { VACATION_REQUEST_STATUS, LEAVE_REQUEST_STATUS } from '@/components/ds/statusCatalogs'
import { MotivoDeLaDecision } from '@/components/MotivoDeLaDecision'
import { Modal } from '@/components/ds/Modal'
import { SaldosDeBeneficios } from '@/modules/payroll/components/SaldosDeBeneficios'
import { formatoDias } from '@/modules/payroll/hooks/useBeneficios'


type Tab = 'vacations' | 'leave'

export default function MyTimeOffPage() {
  const [tab, setTab] = useState<Tab>('vacations')
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <PaginaPortal
      titulo="Vacaciones y permisos"
      bajada="Saldo disponible, solicitudes pendientes y el historial."
      acciones={
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
        >
          <Plus size={15} aria-hidden="true" /> {tab === 'vacations' ? 'Solicitar vacaciones' : 'Solicitar permiso'}
        </button>
      }
    >
      <SegmentGroup<Tab>
        label="Tipo de solicitud"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'vacations', label: 'Vacaciones', icon: <Calendar size={14} aria-hidden="true" /> },
          { value: 'leave', label: 'Permisos', icon: <FileText size={14} aria-hidden="true" /> },
        ]}
        className="self-start"
      />

      {tab === 'vacations' ? <VacationsPanel /> : <LeavePanel />}

      {createOpen && tab === 'vacations' && <CreateVacationModal onClose={() => setCreateOpen(false)} />}
      {createOpen && tab === 'leave' && <CreateLeaveModal onClose={() => setCreateOpen(false)} />}
    </PaginaPortal>
  )
}

function Saldo({ rotulo, valor, destacado = false }: { rotulo: string; valor: string | number | undefined; destacado?: boolean }) {
  return (
    <GlassPanel>
      <p className="text-xs font-semibold text-content-muted">{rotulo}</p>
      <p className={`text-2xl font-bold tabular-nums ${destacado ? 'text-primary' : 'text-content'}`}>
        {dias(valor)} <span className="text-sm font-medium text-content-muted">días</span>
      </p>
    </GlassPanel>
  )
}

function VacationsPanel() {
  const { data: balance } = usePortalVacationBalance()
  const { data: requests, isLoading } = usePortalVacationRequests()
  const cancel = useCancelVacationRequest()
  // `/colaboradores` o `/portal`: la página se monta en los dos.
  const base = usePortalBase()
  const items = requests?.results ?? []

  return (
    <>
      {/* Desglose por bolsa: son derechos distintos y verlos sumados esconde la
          diferencia. ⚠️ NO volver a poner acá que el feriado progresivo se puede
          compensar en dinero: es cierto legalmente (Art. 68) pero compensarlo es una
          decisión de la empresa, no algo que el trabajador active desde el portal, y
          anunciarlo genera una expectativa que esta pantalla no puede resolver. El
          dato sigue en la vista de RRHH (`VacationsTab`), que es quien lo gestiona. */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Saldo rotulo="Disponibles" valor={balance?.total_days_pending} destacado />
        <Saldo rotulo="Feriado legal (Art. 67)" valor={balance?.by_type?.legal?.days_pending} />
        <Saldo rotulo="Feriado progresivo (Art. 68)" valor={balance?.by_type?.progressive?.days_pending} />
        <Saldo rotulo="Tomadas" valor={balance?.total_days_taken} />
      </div>

      <DataTable
        rows={items}
        rowKey={(r: VacationRequestItem) => r.id}
        loading={isLoading}
        minWidth={720}
        caption="Solicitudes de vacaciones"
        empty={<EmptyState icon={<Calendar size={28} aria-hidden="true" />} title="Sin solicitudes de vacaciones" description="Las solicitudes aparecen acá apenas se envían." />}
        columns={[
          { key: 'start_date', header: 'Desde', sortable: true, width: 110, render: (r: any) => fechaCorta(r.start_date) },
          { key: 'end_date', header: 'Hasta', sortable: true, width: 110, render: (r: any) => fechaCorta(r.end_date) },
          {
            key: 'days_requested', header: 'Días', numeric: true, sortable: true, width: 80,
            // ⚠️ Llega como texto: sin esto «10» ordenaría antes que «2».
            sortValue: (r: VacationRequestItem) => parseFloat(String(r.days_requested ?? 0)) || 0,
          },
          {
            key: 'status', header: 'Estado', sortable: true, width: 120,
            render: (r: VacationRequestItem) => (
              <div className="text-center">
                <StatusBadge status={r.status} catalog={VACATION_REQUEST_STATUS} />
                <MotivoDeLaDecision status={r.status} motivo={r.decision_reason} />
                {/* El comprobante de la aprobación, si falta firmarlo (#1489). */}
                {r.comprobante?.firma === 'pending' && r.comprobante.pedido_id && (
                  <Link to={`${base}/signatures?firmar=${r.comprobante.pedido_id}`}
                    className="block text-[11px] text-primary hover:underline mt-1">
                    Firmar comprobante
                  </Link>
                )}
              </div>
            ),
          },
          {
            key: 'notes', header: 'Notas',
            render: (r: VacationRequestItem) => (
              <span className="text-xs text-content-muted">{r.notes || '—'}</span>
            ),
          },
          {
            key: 'cancelar', header: <span className="sr-only">Cancelar</span>, width: 56,
            render: (r: VacationRequestItem) => (
              <div className="text-center">
                {(r.status === 'pending' || r.status === 'pending_second') && (
                  <button
                    onClick={() => {
                      if (confirm('¿Cancelar esta solicitud?')) cancel.mutate(r.id)
                    }}
                    className="p-1 text-content-subtle hover:text-danger-solid rounded hover:bg-soft"
                    title="Cancelar"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ),
          },
        ]}
      />
    </>
  )
}

function LeavePanel() {
  const { data, isLoading } = usePortalLeaveRequests()
  const cancel = useCancelLeaveRequest()
  const items = data?.results ?? []

  return (
    <div className="space-y-4">
    {/* Beneficios de días (#1765): sin beneficios configurados no se dibuja nada. */}
    <SaldosDeBeneficios saldos={data?.beneficios ?? []} />
    <DataTable
      rows={items}
      rowKey={(r: LeaveRequestItem) => r.id}
      loading={isLoading}
      minWidth={820}
      caption="Permisos y licencias solicitados"
      empty={<EmptyState icon={<FileText size={28} aria-hidden="true" />} title="Sin permisos registrados" description="Los permisos y licencias solicitados aparecen acá." />}
      columns={[
        { key: 'leave_type_display', header: 'Tipo', sortable: true },
        { key: 'start_date', header: 'Desde', sortable: true, width: 110, render: (r: any) => fechaCorta(r.start_date) },
        { key: 'end_date', header: 'Hasta', sortable: true, width: 110, render: (r: any) => fechaCorta(r.end_date) },
        {
          key: 'days', header: 'Días', numeric: true, sortable: true, width: 80,
          sortValue: (r: LeaveRequestItem) => parseFloat(String(r.days ?? 0)) || 0,
        },
        {
          key: 'status', header: 'Estado', sortable: true, width: 120,
          render: (r: LeaveRequestItem) => (
            <div className="text-center">
              <StatusBadge status={r.status} catalog={LEAVE_REQUEST_STATUS} />
              <MotivoDeLaDecision status={r.status} motivo={r.decision_reason} />
            </div>
          ),
        },
        {
          key: 'affects_salary', header: 'Afecta sueldo', sortable: true, width: 120,
          sortValue: (r: LeaveRequestItem) => (r.affects_salary ? 1 : 0),
          render: (r: LeaveRequestItem) => (
            <div className="text-center">{r.affects_salary ? 'Sí' : 'No'}</div>
          ),
        },
        {
          key: 'cancelar', header: <span className="sr-only">Cancelar</span>, width: 56,
          render: (r: LeaveRequestItem) => (
            <div className="text-center">
              {(r.status === 'pending' || r.status === 'pending_second') && (
                <button
                  onClick={() => {
                    if (confirm('¿Cancelar este permiso?')) cancel.mutate(r.id)
                  }}
                  className="p-1 text-content-subtle hover:text-danger-solid rounded hover:bg-soft"
                  title="Cancelar"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ),
        },
      ]}
    />
    </div>
  )
}

function CreateVacationModal({ onClose }: { onClose: () => void }) {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [notes, setNotes] = useState('')
  const [vacationType, setVacationType] = useState<'legal' | 'progressive'>('legal')
  const [error, setError] = useState<string | null>(null)
  const create = useCreateVacationRequest()
  const { data: balance } = usePortalVacationBalance()

  const progresivas = parseFloat(balance?.by_type?.progressive?.days_pending ?? '0')
  const disponible = parseFloat(
    balance?.by_type?.[vacationType]?.days_pending ?? balance?.total_days_pending ?? '0'
  )

  // Días HÁBILES calculados en el backend con los feriados del país. Antes se
  // contaban los días CORRIDOS acá en el navegador, así que del 6 al 17 de agosto
  // aparecían 12 cuando el feriado consume 8 — el sábado es siempre inhábil para
  // estos efectos (Art. 69 CT) — y al aprobarse el trabajador perdía 4 días.
  const bd = usePortalBusinessDays(start || undefined, end || undefined)
  const days = bd.data && !bd.data.error ? bd.data.business_days : 0

  const handleSave = () => {
    if (!start || !end) { setError('Faltan las dos fechas'); return }
    if (bd.isFetching) { setError('Falta terminar el cálculo de los días hábiles'); return }
    if (bd.data?.error) { setError(bd.data.error); return }
    if (!days) { setError('El rango no tiene días hábiles'); return }
    setError(null)
    create.mutate(
      {
        start_date: start, end_date: end, days_requested: String(days),
        notes: notes.trim() || undefined, vacation_type: vacationType,
      },
      {
        onSuccess: onClose,
        onError: (e: any) => setError(extractErrorMessage(e, 'Error al solicitar')),
      }
    )
  }

  return (
    <ModalShell title="Solicitar vacaciones" onClose={onClose}
      actions={<ModalFooter onCancel={onClose} onSave={handleSave} pending={create.isPending} disabled={!start || !end} />}>
      <div className="space-y-3">
        {/* Solo se ofrece elegir si de verdad tiene las dos bolsas: un selector
            con una sola opción real es una decisión falsa. */}
        {progresivas > 0 && (
          <div>
            <label htmlFor="mytimeoffpage-de-que-dias-quieres-usar" className="block text-xs font-medium text-content-muted mb-1">
              Bolsa de días a usar
            </label>
            <select id="mytimeoffpage-de-que-dias-quieres-usar"
              value={vacationType}
              onChange={e => setVacationType(e.target.value as 'legal' | 'progressive')}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content"
            >
              <option value="legal">
                Feriado legal — {balance?.by_type?.legal?.days_pending ?? '0'} días
              </option>
              <option value="progressive">
                Feriado progresivo — {balance?.by_type?.progressive?.days_pending ?? '0'} días
              </option>
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="mytimeoffpage-desde-2" className="block text-xs font-medium text-content-muted mb-1">Desde *</label>
            <DatePicker id="mytimeoffpage-desde-2" value={start} onChange={setStart} max={end || undefined} />
          </div>
          <div>
            <label htmlFor="mytimeoffpage-hasta-2" className="block text-xs font-medium text-content-muted mb-1">Hasta *</label>
            <DatePicker id="mytimeoffpage-hasta-2" value={end} onChange={setEnd} min={start || undefined} />
          </div>
        </div>
        {start && end && bd.isFetching && (
          <p className="text-xs text-content-subtle">Calculando días hábiles…</p>
        )}
        {bd.data && !bd.data.error && (
          <div className="text-xs text-content-muted leading-snug">
            <p>
              Se descuentan <strong>{bd.data.business_days}</strong> día
              {bd.data.business_days === 1 ? '' : 's'} hábil
              {bd.data.business_days === 1 ? '' : 'es'}
              {disponible > 0 && <> · disponibles: <strong>{disponible}</strong></>}
            </p>
            <p className="text-content-subtle">
              {bd.data.calendar_days} días corridos · {bd.data.weekend_days} de fin de semana
              {bd.data.holiday_days > 0 && ` · ${bd.data.holiday_days} feriado${bd.data.holiday_days === 1 ? '' : 's'}`}
            </p>
            {bd.data.holidays.length > 0 && (
              <p className="text-content-subtle">
                Feriados en el rango: {bd.data.holidays
                  .map(h => `${h.date.slice(8, 10)}-${h.date.slice(5, 7)} ${h.name}`)
                  .join(' · ')}
              </p>
            )}
          </div>
        )}
        {bd.data?.error && <p className="text-xs text-danger">{bd.data.error}</p>}
        {days > 0 && disponible > 0 && days > disponible && (
          <p className="text-xs text-warning">
            Se piden más días de los disponibles en esa bolsa.
          </p>
        )}
        <div>
          <label htmlFor="mytimeoffpage-notas-opcional" className="block text-xs font-medium text-content-muted mb-1">Notas (opcional)</label>
          <textarea id="mytimeoffpage-notas-opcional" value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content resize-none" />
        </div>
        {error && <p className="text-sm text-danger-solid">{error}</p>}
      </div>
    </ModalShell>
  )
}

function CreateLeaveModal({ onClose }: { onClose: () => void }) {
  const { data } = usePortalLeaveRequests()
  const types = data?.leave_types ?? []
  const beneficios = data?.beneficios ?? []
  // El select mezcla los tipos de permiso y los beneficios de la ficha; un
  // beneficio va como «benefit:<id>» y se manda como leave_type=benefit (#1765).
  const [leaveType, setLeaveType] = useState('')
  const beneficio = leaveType.startsWith('benefit:')
    ? beneficios.find(b => `benefit:${b.beneficio}` === leaveType) : undefined
  const [parteDelDia, setParteDelDia] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [reason, setReason] = useState('')
  const [doc, setDoc] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mediaJornada, setMediaJornada] = useState(false)
  const create = useCreateLeaveRequest()

  // Los permisos se cuentan en dias CORRIDOS, no habiles: la unidad depende del
  // tipo y una licencia medica de 84 dias corridos daria 59 con habiles. Es la
  // decision del #971 y no hay que "corregirla" a habiles.
  const corridos = (() => {
    if (!start || !end) return 0
    const s = new Date(start), e = new Date(end)
    return Math.max(0, Math.floor((e.getTime() - s.getTime()) / 86400000) + 1)
  })()

  // La media jornada solo tiene sentido en un rango de un solo dia: medio dia
  // repartido en una semana no es algo que el sistema pueda representar ni la
  // nomina interpretar.
  const unSoloDia = !!start && !!end && start === end
  const days = unSoloDia && mediaJornada ? 0.5 : corridos

  // Beneficio: los días son HÁBILES (los cuenta el servidor con los feriados del
  // país) y se valida antes de enviar lo que ya se sabe: saldo y fecha permitida.
  const finBeneficio = parteDelDia ? start : end
  const { data: habiles } = usePortalBusinessDays(
    beneficio && start && finBeneficio ? start : undefined,
    beneficio && start && finBeneficio ? finBeneficio : undefined)
  const diasBeneficio = parteDelDia ? 0.5 : (habiles?.business_days ?? 0)
  const disponibleBeneficio = beneficio ? parseFloat(beneficio.disponible) : 0
  const fechasValidas = beneficio?.fechas_validas ?? null
  const problemaBeneficio = !beneficio ? null
    : disponibleBeneficio <= 0 ? 'No quedan días disponibles en el período.'
      : fechasValidas && fechasValidas.length === 0
        ? 'No hay una fecha válida en el período: consultar a RR. HH.'
        : fechasValidas && start && !fechasValidas.includes(start)
          ? `Solo se puede tomar ${fechasValidas.length === 1 ? 'el' : 'uno de estos días:'} ${fechasValidas.map(fechaCorta).join(', ')}.`
          : diasBeneficio > disponibleBeneficio
            ? `Quedan ${formatoDias(disponibleBeneficio)} días disponibles.`
            : null
  const faltaMotivo = !!beneficio?.requiere_motivo && !reason.trim()
  const listo = beneficio
    ? !!start && !!finBeneficio && !problemaBeneficio && !faltaMotivo
    : !!leaveType && !!start && !!end

  // Con una sola fecha posible (el cumpleaños) se propone directamente.
  useEffect(() => {
    if (fechasValidas && fechasValidas.length === 1) {
      setStart(fechasValidas[0])
      setEnd(fechasValidas[0])
    }
    setParteDelDia('')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveType])

  // Si el rango deja de ser de un solo dia, la casilla se apaga sola: dejarla
  // marcada mandaria 0,5 sobre un rango largo, que el backend rechaza.
  useEffect(() => {
    if (!unSoloDia && mediaJornada) setMediaJornada(false)
  }, [unSoloDia, mediaJornada])

  const handleSave = () => {
    if (!listo) {
      setError(problemaBeneficio || (faltaMotivo ? 'Falta el motivo.' : 'Faltan campos obligatorios'))
      return
    }
    setError(null)
    create.mutate(
      beneficio
        ? {
            leave_type: 'benefit', beneficio: beneficio.beneficio,
            start_date: start, end_date: finBeneficio,
            parte_del_dia: parteDelDia, reason: reason.trim() || undefined,
            document: doc ?? undefined,
          }
        : {
            leave_type: leaveType, start_date: start, end_date: end,
            days: String(days), reason: reason.trim() || undefined,
            document: doc ?? undefined,
          },
      {
        onSuccess: onClose,
        onError: (e: any) => setError(extractErrorMessage(e, 'Error al solicitar')),
      }
    )
  }

  return (
    <ModalShell title="Solicitar permiso" onClose={onClose}
      actions={<ModalFooter onCancel={onClose} onSave={handleSave} pending={create.isPending} disabled={!listo} />}>
      <div className="space-y-3">
        <div>
          <label htmlFor="mytimeoffpage-tipo-de-permiso" className="block text-xs font-medium text-content-muted mb-1">Tipo de permiso *</label>
          <select id="mytimeoffpage-tipo-de-permiso" value={leaveType} onChange={e => setLeaveType(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
            <option value="">— Seleccionar —</option>
            {beneficios.length > 0 && (
              <optgroup label="Beneficios de días">
                {beneficios.map(b => (
                  <option key={b.beneficio} value={`benefit:${b.beneficio}`}>
                    {b.nombre} ({formatoDias(b.disponible)} disponibles)
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Permisos">
              {types.map((t: LeaveTypeOption) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
        {beneficio && (
          <div className="rounded-lg border border-divider bg-glass-3 px-3 py-2 text-xs text-content-muted space-y-1">
            <p>
              Disponibles: <strong className="text-content">{formatoDias(beneficio.disponible)}</strong>
              {' '}de {formatoDias(parseFloat(beneficio.asignado) + parseFloat(beneficio.arrastre))}
              {parseFloat(beneficio.en_tramite) > 0 && <> · {formatoDias(beneficio.en_tramite)} en trámite</>}
            </p>
            {fechasValidas && fechasValidas.length > 0 && (
              <p>
                {fechasValidas.length === 1 ? 'Fecha permitida' : 'Fechas permitidas'}:{' '}
                {fechasValidas.map(f => (
                  <button key={f} type="button" onClick={() => { setStart(f); setEnd(f) }}
                    className={`mr-1 underline-offset-2 hover:underline ${f === start ? 'font-semibold text-content' : 'text-primary'}`}>
                    {fechaCorta(f)}
                  </button>
                ))}
              </p>
            )}
            {!fechasValidas && <p>Se descuentan los días hábiles del rango.</p>}
          </div>
        )}
        {beneficio?.permite_medio_dia && (
          <div>
            <label htmlFor="mytimeoffpage-jornada" className="block text-xs font-medium text-content-muted mb-1">Jornada</label>
            <select id="mytimeoffpage-jornada" value={parteDelDia} onChange={e => setParteDelDia(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
              <option value="">Días completos</option>
              <option value="manana">Medio día (mañana)</option>
              <option value="tarde">Medio día (tarde)</option>
            </select>
          </div>
        )}
        <div className={`grid ${beneficio && parteDelDia ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
          <div>
            <label htmlFor="mytimeoffpage-desde" className="block text-xs font-medium text-content-muted mb-1">
              {beneficio && parteDelDia ? 'Fecha *' : 'Desde *'}
            </label>
            <DatePicker id="mytimeoffpage-desde" value={start} onChange={setStart}
              max={beneficio && parteDelDia ? undefined : end || undefined} />
          </div>
          {!(beneficio && parteDelDia) && <div>
            <label htmlFor="mytimeoffpage-hasta" className="block text-xs font-medium text-content-muted mb-1">Hasta *</label>
            <DatePicker id="mytimeoffpage-hasta" value={end} onChange={setEnd} min={start || undefined} />
          </div>}
        </div>
        {!beneficio && unSoloDia && (
          <label className="flex items-center gap-2 text-xs text-content cursor-pointer">
            <input type="checkbox" checked={mediaJornada}
              onChange={e => setMediaJornada(e.target.checked)}
              className="rounded border-input-border" />
            Es media jornada (medio día)
          </label>
        )}
        {!beneficio && days > 0 && (
          <p className="text-xs text-content-muted">
            Total: <strong>{days.toLocaleString('es-CL', { maximumFractionDigits: 1 })}</strong>
            {' '}día{days === 1 ? '' : 's'}
          </p>
        )}
        {beneficio && diasBeneficio > 0 && (
          <p className="text-xs text-content-muted">
            Se descuentan <strong>{formatoDias(diasBeneficio)}</strong> {diasBeneficio === 1 ? 'día hábil' : 'días hábiles'}.
          </p>
        )}
        {beneficio && problemaBeneficio && start && (
          <p role="alert" className="text-xs text-warning-content-soft">{problemaBeneficio}</p>
        )}
        <div>
          <label htmlFor="mytimeoffpage-motivo-opcional" className="block text-xs font-medium text-content-muted mb-1">
            {beneficio?.requiere_motivo ? 'Motivo *' : 'Motivo (opcional)'}
          </label>
          <textarea id="mytimeoffpage-motivo-opcional" value={reason} onChange={e => setReason(e.target.value)} rows={2}
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content resize-none" />
        </div>
        <div>
          <label htmlFor="mytimeoffpage-documento-adjunto-opcional" className="block text-xs font-medium text-content-muted mb-1">Documento adjunto (opcional)</label>
          <input id="mytimeoffpage-documento-adjunto-opcional" type="file" onChange={e => setDoc(e.target.files?.[0] ?? null)}
            className="text-xs text-content-muted" />
          <p className="text-xs text-content-subtle mt-1">Para licencia médica, certificados, etc.</p>
        </div>
        {error && <p className="text-sm text-danger-solid">{error}</p>}
      </div>
    </ModalShell>
  )
}

// El pie va en `actions` del Modal y no dentro del cuerpo: dentro, con su
// `-mx-6` (24 px) contra los 18 px de padding del cuerpo, desbordaba y el modal
// mostraba una barra de scroll horizontal (2026-10-05).
function ModalShell({ title, onClose, actions, children }: {
  title: string; onClose: () => void; actions: React.ReactNode; children: React.ReactNode
}) {
  return (
    <Modal open onClose={onClose} title={title} size="md" actions={actions}>
      <div>{children}</div>
    </Modal>
  )
}

function ModalFooter({ onCancel, onSave, pending, disabled }: {
  onCancel: () => void; onSave: () => void; pending: boolean; disabled: boolean
}) {
  return (
    <div className="flex justify-end gap-3">
      <button onClick={onCancel} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
        Cancelar
      </button>
      <button onClick={onSave} disabled={pending || disabled}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50">
        {pending && <Loader2 size={14} className="animate-spin" />}
        Solicitar
      </button>
    </div>
  )
}
